import React, { useState } from 'react';
import { Modal, Button, Badge, Tabs, Flex, Tag } from 'antd';
import {
  CheckOutlined,
  DeleteOutlined,
  CloseOutlined,
  ClockCircleOutlined,
  BellOutlined,
  ExportOutlined,
  MessageOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { format } from 'date-fns';
import styles from './notification-modal.module.scss';
import { getTimeAgo } from 'util/time-ago';
import { setMessageType, setSelectedLead, toggleMessage } from 'redux/spot/spot-actions';

const NotificationItem = ({ notification, handleMarkAsRead, handleDelete }) => {
  const getIcon = () => {
    switch (notification.type) {
      case 'lead_assigned':
        return <BellOutlined className={styles.leadIcon} />;
      case 'tasks':
        return <ClockCircleOutlined className={styles.taskIcon} />;
      default:
        return <div className={`${styles.iconCircle} ${styles.leadIcon}`} />;
    }
  };

  return (
    <div className={`${styles.notificationItem} ${notification.read_at ? '' : styles.unread}`}>
      <div className={`${styles.notificationContent} ${notification.type}`}>
        {getIcon()}
        <div className={styles.notificationDetails}>
          <div className={styles.notificationHeader}>
            <div className={styles.titleContainer}>
              <h4>{notification.message}</h4>
              {notification.priority && (
                <span className={`${styles.priorityBadge} ${styles[notification.priority]}`}>
                  {notification.priority}
                </span>
              )}
            </div>
            <span className={styles.timeAgo}>{notification.created_at ? getTimeAgo(notification.created_at) : ''}</span>
          </div>
          <Flex justify="space-between" align="center">
            <p className={styles.timestamp}>
              {notification.created_at ? format(new Date(notification.created_at), 'MMM d, h:mm a') : ''}
            </p>
            {!notification.read_at && (
              <Button
                type="text"
                className={styles.markAsReadButton}
                icon={<CheckOutlined />}
                onClick={() => handleMarkAsRead(notification.id)}>
                Mark as read
              </Button>
            )}
            <Button
              type="text"
              className={styles.deleteButton}
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(notification.id)}
            />
          </Flex>
        </div>
      </div>
    </div>
  );
};

/**
 * NotificationModal
 *
 * Modal that displays notifications grouped by type, with actions to mark as read or delete.
 *
 * @param {Object} props - Component properties
 * @param {boolean} props.visible - Whether the modal is visible
 * @param {function} props.onClose - Handler to close the modal
 */
export const NotificationModal = ({
  visible,
  onClose,
  notifications,
  markAsRead,
  batchNotificationEdit,
  deleteNotification,
  messages,
  messagesUnread = 0,
}) => {
  const [activeTab, setActiveTab] = useState('all');

  const getFilteredNotifications = () => {
    if (activeTab === 'all') return notifications;
    return notifications.filter((notification) => notification.type === activeTab);
  };

  const getCounts = () => {
    const counts = {
      all: notifications.filter((n) => !n.read_at).length,
      leads: notifications.filter((n) => n.type === 'lead_assigned' && !n.read_at).length,
      tasks: notifications.filter((n) => n.type === 'task_reminder' && !n.read_at).length,
    };
    return counts;
  };

  const handleMarkAsRead = async (id) => {
    await markAsRead(id);
  };

  const handleDelete = async (id) => {
    await deleteNotification(id);
  };

  const getIdsAvailable = (key) => {
    return notifications.filter((notification) => !notification[key]).map((notification) => notification.id);
  };

  const handleBatchEdit = async (key) => {
    const ids = getIdsAvailable(key);

    if (ids.length === 0) return;
    await batchNotificationEdit(ids, key);
  };

  const counts = getCounts();
  const items = [
    {
      key: 'all',
      label: (
        <span>
          All <Badge count={counts.all} className={styles.allBadge} />
        </span>
      ),
    },
    {
      key: 'lead_assigned',
      label: (
        <span>
          Leads <Badge count={counts.leads} className={styles.leadBadge} />
        </span>
      ),
    },
    {
      key: 'task_reminder',
      label: (
        <span>
          Tasks <Badge count={counts.tasks} className={styles.taskBadge} />
        </span>
      ),
    },
    {
      key: 'messages',
      label: (
        <span>
          Messages <Badge count={messagesUnread} className={styles.leadBadge} />
        </span>
      ),
    },
  ];

  return (
    <Modal
      title={
        <div className={styles.modalHeader}>
          <div className={styles.titleWithBadge}>
            <span>Notifications</span>
          </div>
          <div className={styles.headerActions}>
            {activeTab !== 'messages' && (
              <>
                {getIdsAvailable('read_at').length > 0 && (
                  <Button
                    icon={<CheckOutlined />}
                    onClick={() => handleBatchEdit('read_at')}
                    type="text"
                    className={styles.headerButton}>
                    Mark all as read
                  </Button>
                )}
                {getIdsAvailable('deleted_at').length > 0 && (
                  <Button
                    icon={<DeleteOutlined />}
                    onClick={() => handleBatchEdit('deleted_at')}
                    type="text"
                    className={styles.headerButton}>
                    Delete all
                  </Button>
                )}
              </>
            )}
            <Button type="text" icon={<CloseOutlined />} onClick={onClose} className={styles.closeButton} />
          </div>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={600}
      footer={null}
      className={styles.notificationModal}
      closable={false}>
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={items} className={styles.tabs} centered type="card" />
      <div className={styles.notificationList}>
        {activeTab !== 'messages' ? (
          <NotificationList
            getFilteredNotifications={getFilteredNotifications}
            handleDelete={handleDelete}
            handleMarkAsRead={handleMarkAsRead}
          />
        ) : messagesUnread > 0 ? (
          <MessagesList messages={messages} onClose={onClose} />
        ) : (
          <div className={styles.emptyState}>No Messages</div>
        )}
      </div>
    </Modal>
  );
};

const NotificationList = ({ getFilteredNotifications, handleMarkAsRead, handleDelete }) => {
  return getFilteredNotifications().length > 0 ? (
    getFilteredNotifications().map((notification) => (
      <NotificationItem
        key={notification.id}
        notification={notification}
        handleMarkAsRead={handleMarkAsRead}
        handleDelete={handleDelete}
      />
    ))
  ) : (
    <div className={styles.emptyState}>No notifications</div>
  );
};

const MessagesList = ({ messages, onClose }) => {
  const handleOpenMessage = ({ leadId, school_id, school_name, messageType, parent_first_name, parent_last_name }) => {
    setSelectedLead({ id: leadId, school_id, school_name, parent_first_name, parent_last_name });
    setMessageType(messageType);
    onClose();
    toggleMessage();
  };

  return messages.map((item, idx) => (
    <div key={idx} className={`${styles.notificationItem}`}>
      <Flex justify="space-between" align="center">
        <Tag color="geekblue">{item.name}</Tag>
        <span className={styles.timeAgo}>{getTimeAgo(item.last_message_at)}</span>
      </Flex>
      <Flex align="center" gap={16}>
        {item.communication_type.toLowerCase() === 'sms' ? (
          <MessageOutlined className={styles.leadIcon} />
        ) : (
          <MailOutlined className={styles.leadIcon} />
        )}
        <p className="spacing-my-16">
          {Number(item.unread_count) === 1 ? `You have a new message ` : `You have ${item.unread_count} new messages `}
          from {item.parent_first_name} {item.parent_last_name}
        </p>
      </Flex>
      <Flex justify="flex-end">
        <Button
          size="small"
          variant="filled"
          type="text"
          onClick={() =>
            handleOpenMessage({
              leadId: item.lead_id,
              school_id: item.school_id,
              school_name: item.name,
              messageType: item.communication_type.toLowerCase() || 'sms',
              parent_first_name: item.parent_first_name,
              parent_last_name: item.parent_last_name,
            })
          }
          icon={<ExportOutlined />}>
          See Conversation
        </Button>
      </Flex>
    </div>
  ));
};
